import React from 'react';

import { Icon } from '@/nova/components';
import { Sheet } from '@/nova/components/Sheet';

import type { CreativeItem } from '../../creative-api';
import {
  PickerCard,
  PickerArt,
  PickerEmpty,
  PickerFoot,
  PickerGrid,
  PickerHint,
  PickerImg,
  PickerTitle,
  PickCheck,
} from './PoolPickerSheet.styles';

export interface PoolPickerSheetProps {
  /** Dialog title (i18n key). */
  title: string;
  /** The banners the owner can pick from (the brand's promotion/creative pool). */
  poolItems: CreativeItem[];
  /** Banner ids currently selected (drives the check + the disabled-at-max gate). */
  selectedIds: string[];
  /** How to label a card (the consumer owns the fallback chain, e.g. headline > internal name > id). */
  labelOf: (item: CreativeItem) => string;
  /** Toggle a banner in/out of the selection. The consumer self-gates the max (see {@link max}). */
  onToggle: (bannerId: string) => void;
  onClose: () => void;
  /** Slide-out close handle (from `useSheetDismiss`) so footers/actions animate the exit. */
  closeHandle?: React.MutableRefObject<(() => void) | null>;
  /** Cap on the selection: unselected cards are disabled once this many are picked. */
  max?: number;
  /** Copy shown when the pool is empty (i18n key). */
  emptyText: string;
  /** Optional one-line hint above the grid (i18n key). */
  hint?: string;
  /** Optional Sheet footer (e.g. a confirm row when the picker is a step, not a live edit). */
  footer?: React.ReactNode;
}

/**
 * A reusable multi-select picker over a brand's banner pool, rendered as a Sheet body: a responsive grid
 * of artwork cards, each toggling in/out of the selection with a check badge. Used both by the carousel
 * curation (add banners to a carousel) and the storyboard composer (pick the promotions a video advertises).
 * Purely presentational: the selection + toggle live in the consumer, which also supplies the card label
 * and the max-selection cap.
 */
export const PoolPickerSheet = ({
  title,
  poolItems,
  selectedIds,
  labelOf,
  onToggle,
  onClose,
  closeHandle,
  max,
  emptyText,
  hint,
  footer,
}: PoolPickerSheetProps): React.ReactElement => {
  const atMax = max != null && selectedIds.length >= max;
  return (
    <Sheet title={title} ariaLabel={title} onClose={onClose} closeHandle={closeHandle} footer={footer}>
      {poolItems.length === 0 ? (
        <PickerEmpty text={emptyText} />
      ) : (
        <>
          {hint ? <PickerHint text={hint} /> : null}
          <PickerGrid>
            {poolItems.map((item) => {
              const on = selectedIds.includes(item.banner_id);
              const disabled = !on && atMax;
              return (
                <PickerCard
                  key={item.banner_id}
                  type="button"
                  $selected={on}
                  $disabled={disabled}
                  aria-pressed={on}
                  aria-disabled={disabled}
                  onClick={() => {
                    if (disabled) return; // handler self-gates the max (styling alone does not block a click)
                    onToggle(item.banner_id);
                  }}
                >
                  <PickerArt>
                    {item.image_url ? <PickerImg src={item.image_url} alt="" loading="lazy" /> : null}
                  </PickerArt>
                  <PickerFoot>
                    <PickerTitle text={labelOf(item)} />
                    {on ? (
                      <PickCheck aria-hidden="true">
                        <Icon name="check" size={13} />
                      </PickCheck>
                    ) : null}
                  </PickerFoot>
                </PickerCard>
              );
            })}
          </PickerGrid>
        </>
      )}
    </Sheet>
  );
};
